import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, limit, addDoc, serverTimestamp, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { badgeDefinitions, getBadgeDefinition } from '../utils/badgeDefinitions';

interface FeedEvent {
  id: string;
  type: 'heart_sent' | 'daily_goal_reached' | 'weekly_goal_reached' | 'badge_earned' | 'practice_reminder' | 'unknown';
  fromUserId: string;
  fromUserNickname: string;
  toUserId?: string; // For heart/nudge events
  toUserNickname?: string; // For heart/nudge events
  badgeName?: string; // For badge events
  timestamp: any;
  hearts: string[]; // Users who sent hearts to this event
  nudges: string[]; // Users who sent nudges to this event
}

const Feed: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [feedEvents, setFeedEvents] = useState<FeedEvent[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [buddies, setBuddies] = useState<any[]>([]);
  const [usersStartedPractice, setUsersStartedPractice] = useState<Set<string>>(new Set());
  const [sentHearts, setSentHearts] = useState<Set<string>>(new Set());
  const [sentNudges, setSentNudges] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  
  // Filter state
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'actioned' | 'unactioned' | 'badge_earned' | 'daily_goal_reached' | 'weekly_goal_reached' | 'practice_reminder'>('all');

  // Load user's buddies
  const loadBuddies = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDocs(query(collection(db, 'users'), where('linkedBuddies', 'array-contains', currentUser.uid)));
      const buddyData = userDoc.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      setBuddies(buddyData);
    } catch (error) {
      console.error('Error loading buddies:', error);
    }
  };

  // Clean up invalid feed events (utility function)
  const cleanupInvalidFeedEvents = async () => {
    if (!currentUser) return;
    
    try {
      const validTypes = ['heart_sent', 'nudge_sent', 'daily_goal_reached', 'weekly_goal_reached', 'badge_earned', 'practice_reminder'];
      
      // Get all feed events
      const allEventsQuery = query(collection(db, 'feedEvents'), limit(1000));
      const allEventsSnapshot = await getDocs(allEventsQuery);
      
      const invalidEvents: string[] = [];
      allEventsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (!validTypes.includes(data.type) || !data.fromUserId || !data.fromUserNickname) {
          invalidEvents.push(doc.id);
        }
      });
      
      if (invalidEvents.length > 0) {
        console.log(`Found ${invalidEvents.length} invalid feed events to clean up`);
        // Note: In production, you might want to delete these events
        // For now, just log them for debugging
        console.log('Invalid event IDs:', invalidEvents);
      }
    } catch (error) {
      console.error('Error cleaning up invalid feed events:', error);
    }
  };

  // Load feed events
  const loadFeedEvents = async () => {
    setLoadingFeed(true);
    try {
      if (!currentUser || buddies.length === 0) {
        setFeedEvents([]);
        return;
      }

      // Get current user's buddy linking timestamps
      const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const currentUserData = currentUserDoc.data();
      const buddyLinkedAt = currentUserData?.buddyLinkedAt || {};

      // Get all buddy IDs including current user
      const buddyIds = [currentUser.uid, ...buddies.map(buddy => buddy.uid)];
      
      // Query feed events where fromUserId is in our buddy network (simplified to avoid index requirements)
      const q = query(
        collection(db, 'feedEvents'),
        where('fromUserId', 'in', buddyIds),
        limit(200) // Increased limit since we'll filter by date client-side
      );

      const querySnapshot = await getDocs(q);
      const events: FeedEvent[] = [];
      
      // Filter for last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const validTypes = ['heart_sent', 'daily_goal_reached', 'weekly_goal_reached', 'badge_earned', 'practice_reminder'];
        
        // Skip events older than 7 days
        const eventTime = data.timestamp?.toDate?.() || new Date(data.timestamp || 0);
        if (eventTime < sevenDaysAgo) {
          return;
        }
        
        // Skip heart_sent events to prevent endless loops
        if (data.type === 'heart_sent') {
          return;
        }
        
        // Skip practice reminders where the current user is the target (users don't nudge themselves)
        if (data.type === 'practice_reminder' && data.toUserId === currentUser.uid) {
          return;
        }
        
        // Skip badge awards where the current user is the recipient (they already see the celebration popup)
        if (data.type === 'badge_earned' && data.fromUserId === currentUser.uid) {
          return;
        }
        
        // Only process events with valid types
        if (!data.type || !validTypes.includes(data.type)) {
          console.warn('Skipping feed event with invalid type:', {
            id: doc.id,
            type: data.type,
            fromUserId: data.fromUserId,
            fromUserNickname: data.fromUserNickname
          });
          return;
        }

        // Filter events based on buddy linking time
        // Only show events from buddies that occurred after they were linked
        const eventFromUserId = data.fromUserId;
        if (eventFromUserId !== currentUser.uid) {
          const buddyLinkedTime = buddyLinkedAt[eventFromUserId];
          if (buddyLinkedTime) {
            const eventTime = data.timestamp?.toDate?.() || new Date(data.timestamp || 0);
            const linkedTime = buddyLinkedTime.toDate?.() || new Date(buddyLinkedTime);
            
            // Skip events that occurred before buddy linking
            if (eventTime < linkedTime) {
              return;
            }
          }
        }
        
        // Validate and clean the event data
        const event: FeedEvent = {
          id: doc.id,
          type: data.type,
          fromUserId: data.fromUserId || '',
          fromUserNickname: data.fromUserNickname || 'Unknown User',
          toUserId: data.toUserId || undefined,
          toUserNickname: data.toUserNickname || undefined,
          badgeName: data.badgeName || undefined,
          timestamp: data.timestamp || null,
          hearts: data.hearts || [],
          nudges: data.nudges || []
        };
        
        // Only add events with complete critical data
        if (event.fromUserId && event.fromUserNickname) {
          events.push(event);
        } else {
          console.warn('Skipping feed event with missing critical data:', {
            id: doc.id,
            type: event.type,
            fromUserId: event.fromUserId,
            fromUserNickname: event.fromUserNickname
          });
        }
      });

      // Sort by timestamp in JavaScript to avoid needing composite index
      events.sort((a, b) => {
        const aTime = a.timestamp?.toDate?.()?.getTime() || new Date(a.timestamp || 0).getTime();
        const bTime = b.timestamp?.toDate?.()?.getTime() || new Date(b.timestamp || 0).getTime();
        return bTime - aTime; // Descending order (newest first)
      });

      setFeedEvents(events);
    } catch (error) {
      console.error('Error loading feed events:', error);
    } finally {
      setLoadingFeed(false);
    }
  };

  // Send heart to a user
  const sendHeart = async (userId: string, eventId: string) => {
    if (!currentUser) return;
    
    try {
      // Find the event to add heart to
      const event = feedEvents.find(e => e.id === eventId);
      if (!event) return;

      // Check if user already sent a heart to this event
      const currentHearts = event.hearts || [];
      if (currentHearts.includes(currentUser.uid)) {
        console.log('User already sent a heart to this event');
        return;
      }

      // Update the event's hearts array in Firestore
      const eventRef = doc(db, 'feedEvents', eventId);
      const updatedHearts = [...currentHearts, currentUser.uid];
      
      await updateDoc(eventRef, {
        hearts: updatedHearts
      });

      // Track that we've sent a heart to this specific event for immediate UI feedback
      setSentHearts(prev => {
        const newSet = new Set(prev);
        newSet.add(eventId);
        return newSet;
      });
      
      // Update user's hearts received
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        heartsReceived: arrayUnion(currentUser.uid)
      });

      console.log('Heart sent successfully');
    } catch (error) {
      console.error('Error sending heart:', error);
    }
  };

  // Send nudge to a user
  const sendNudge = async (userId: string, eventId: string) => {
    if (!currentUser) return;
    
    try {
      const buddy = buddies.find(b => b.uid === userId);
      const buddyName = buddy?.nickname || 'Unknown';
      
      // Track that we've sent a nudge to this specific event for immediate UI feedback
      setSentNudges(prev => {
        const newSet = new Set(prev);
        newSet.add(eventId);
        return newSet;
      });
      
      // Create a nudge notification for the target user
      const nudgeNotification = {
        id: `nudge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'nudge_received',
        fromUserId: currentUser.uid,
        fromUserNickname: currentUser.nickname || 'Unknown',
        toUserId: userId,
        message: `${currentUser.nickname || 'Someone'} sent you a nudge to help keep you on track with practice!`,
        reason: 'missed_practice',
        timestamp: serverTimestamp(),
        read: false
      };

      // Add notification to target user's notifications collection
      await addDoc(collection(db, 'notifications'), nudgeNotification);

      // Update the feed event to track that this user has sent a nudge
      const eventRef = doc(db, 'feedEvents', eventId);
      await updateDoc(eventRef, {
        nudges: arrayUnion(currentUser.uid)
      });

      // Update user's nudges received count
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        nudgesReceived: arrayUnion(currentUser.uid)
      });

      // No browser notification needed - button already shows "Nudge sent!" feedback

      console.log('Nudge sent successfully');
    } catch (error) {
      console.error('Error sending nudge:', error);
      // Remove from sent nudges if there was an error
      setSentNudges(prev => {
        const newSet = new Set(prev);
        newSet.delete(eventId);
        return newSet;
      });
    }
  };

  // Filter events based on selected filter
  const getFilteredEvents = () => {
    let filtered = [...feedEvents];
    
    switch (selectedFilter) {
      case 'actioned':
        // Show events where user has sent heart or nudge
        filtered = filtered.filter(event => {
          const hasHearted = event.hearts && event.hearts.includes(currentUser?.uid || '');
          const hasNudged = event.nudges && event.nudges.includes(currentUser?.uid || '');
          return hasHearted || hasNudged;
        });
        break;
      case 'unactioned':
        // Show events where user hasn't sent heart or nudge
        filtered = filtered.filter(event => {
          const hasHearted = event.hearts && event.hearts.includes(currentUser?.uid || '');
          const hasNudged = event.nudges && event.nudges.includes(currentUser?.uid || '');
          return !hasHearted && !hasNudged;
        });
        break;
      case 'badge_earned':
        filtered = filtered.filter(event => event.type === 'badge_earned');
        break;
      case 'daily_goal_reached':
        filtered = filtered.filter(event => event.type === 'daily_goal_reached');
        break;
      case 'weekly_goal_reached':
        filtered = filtered.filter(event => event.type === 'weekly_goal_reached');
        break;
      case 'practice_reminder':
        filtered = filtered.filter(event => event.type === 'practice_reminder');
        break;
      default:
        // 'all' - no filtering
        break;
    }
    
    return filtered;
  };

  // Get paginated events
  const getPaginatedEvents = () => {
    const filtered = getFilteredEvents();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      events: filtered.slice(startIndex, endIndex),
      totalPages: Math.ceil(filtered.length / itemsPerPage),
      totalItems: filtered.length
    };
  };

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilter]);

  // Check if a user has started practice today
  const hasUserStartedPracticeToday = async (userId: string): Promise<boolean> => {
    try {
      const today = new Date().toISOString().split('T')[0];
      // Simplified query to avoid index requirements
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', userId)
      );
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      
      // Filter sessions to only include today's sessions
      const todaysSessions = sessionsSnapshot.docs.filter(doc => {
        const sessionData = doc.data();
        const startAt = sessionData.startAt?.toDate?.() || new Date(sessionData.startAt || 0);
        const sessionDate = startAt.toISOString().split('T')[0];
        return sessionDate === today;
      });
      
      return todaysSessions.length > 0;
    } catch (error) {
      console.error('Error checking practice status:', error);
      return false;
    }
  };

  // Check practice status for all users with practice_reminder events
  const checkPracticeStatusForReminders = async () => {
    if (!feedEvents.length) return;
    
    const reminderEvents = feedEvents.filter(event => event.type === 'practice_reminder');
    if (reminderEvents.length === 0) return;
    
    const userIds = reminderEvents.map(event => event.toUserId!).filter(Boolean);
    const practiceStatuses = await Promise.all(
      userIds.map(async (userId) => ({
        userId,
        hasStarted: await hasUserStartedPracticeToday(userId)
      }))
    );
    
    const startedUsers = new Set(
      practiceStatuses
        .filter(status => status.hasStarted)
        .map(status => status.userId)
    );
    
    setUsersStartedPractice(startedUsers);
  };

  // Format timestamp
  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return '';
    
    let date;
    try {
      date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (isNaN(date.getTime())) {
        return 'unknown time';
      }
    } catch (error) {
      return 'unknown time';
    }
    
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const initializeData = async () => {
      await loadBuddies();
      // Clean up any invalid feed events on component load
      await cleanupInvalidFeedEvents();
      setLoading(false);
    };

    initializeData();
  }, [currentUser, navigate]);

  useEffect(() => {
    if (buddies.length > 0) {
      loadFeedEvents();
    }
  }, [buddies]);

  // Check practice status when feed events change
  useEffect(() => {
    checkPracticeStatusForReminders();
  }, [feedEvents]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Navigation */}
      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-0">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm mr-4"
              >
                ← Back to Dashboard
              </button>
              <div className="flex items-center">
                <div className="text-3xl mr-3">🏠</div>
                <h1 className="text-2xl font-bold text-white">
                  Practice Feed
                </h1>
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => navigate('/social')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                👥 View Buddies
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="px-4 py-6 sm:px-0">
        <div className="max-w-4xl mx-auto">
          {/* Header Card */}
          <div className="bg-white shadow rounded-lg py-6 px-6 mb-8">
            <div className="text-center">
              <p className="text-lg text-gray-600">See what your practice buddies have been up to!</p>
            </div>
          </div>

          {/* Feed Content */}
          {loadingFeed ? (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-center">
                <div className="loading-spinner mb-2"></div>
                <p className="text-gray-600">Loading feed...</p>
              </div>
            </div>
          ) : feedEvents.length === 0 ? (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg shadow-sm p-6 border border-indigo-100">
              <div className="text-center">
                <div className="text-indigo-600 text-4xl mb-4">🎵</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No updates yet
                </h3>
                <p className="text-gray-600 mb-6">Add some practice buddies to see their achievements!</p>
                <button
                  onClick={() => navigate('/social')}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Add Your First Buddy
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm">
              {/* Filter and Pagination Controls */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Filter Dropdown */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Filter:</label>
                    <select
                      value={selectedFilter}
                      onChange={(e) => setSelectedFilter(e.target.value as any)}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Events</option>
                      <option value="actioned">Actioned (Hearted/Nudged)</option>
                      <option value="unactioned">Unactioned</option>
                      <option value="badge_earned">Badge Awards</option>
                      <option value="daily_goal_reached">Daily Goals</option>
                      <option value="weekly_goal_reached">Weekly Goals</option>
                      <option value="practice_reminder">Practice Reminders</option>
                    </select>
                  </div>
                  
                  {/* Pagination Info */}
                  <div className="text-sm text-gray-600">
                    {(() => {
                      const { totalItems, totalPages } = getPaginatedEvents();
                      return `${totalItems} events • Page ${currentPage} of ${totalPages}`;
                    })()}
                  </div>
                </div>
              </div>
              
              <div className="space-y-1 p-2">
                {(() => {
                  const { events } = getPaginatedEvents();
                  return events.map((event) => (
                  <div key={event.id} className="bg-white rounded-lg shadow-sm py-2 px-3 hover:shadow-md transition-shadow">
                    {/* Desktop Layout - Horizontal */}
                    <div className="hidden sm:flex items-center space-x-3">
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {event.fromUserNickname.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      
                      {/* Icon */}
                      <div className="flex-shrink-0">
                        {event.type === 'heart_sent' && <span className="text-lg">💖</span>}
                        {event.type === 'daily_goal_reached' && <span className="text-lg">🎯</span>}
                        {event.type === 'weekly_goal_reached' && <span className="text-lg">🏆</span>}
                        {event.type === 'badge_earned' && <span className="text-lg">🏅</span>}
                        {event.type === 'practice_reminder' && <span className="text-lg">⏰</span>}
                        {!['heart_sent', 'daily_goal_reached', 'weekly_goal_reached', 'badge_earned', 'practice_reminder'].includes(event.type) && <span className="text-lg">📢</span>}
                      </div>
                      
                      {/* Message */}
                      <div className="flex-1 min-w-0">
                        {event.type === 'heart_sent' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> sent a heart to <span className="font-semibold">{event.toUserNickname}</span>
                          </span>
                        )}
                        {event.type === 'daily_goal_reached' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> reached their daily goal! 🎯
                          </span>
                        )}
                        {event.type === 'weekly_goal_reached' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> completed their weekly goal! 🏆
                          </span>
                        )}
                        {event.type === 'badge_earned' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> earned {event.badgeName ? (
                              <>the <span className="font-semibold">
                                {(() => {
                                  const badgeDef = getBadgeDefinition(event.badgeName);
                                  
                                  return badgeDef ? (
                                    <span className="relative group">
                                      <span className="cursor-help underline decoration-dotted">{event.badgeName}</span>
                                      {/* Tooltip */}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                        {badgeDef.emoji} {badgeDef.description}
                                      </div>
                                    </span>
                                  ) : (
                                    <span className="text-orange-600" title={`No definition found for: ${event.badgeName}`}>
                                      {event.badgeName}
                                    </span>
                                  );
                                })()}
                              </span> badge</>
                            ) : (
                              'a new badge'
                            )}! 🏅
                          </span>
                        )}
                        {event.type === 'practice_reminder' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.toUserNickname}</span> hasn't started their scheduled practice yet. Give them a nudge! ⏰
                          </span>
                        )}
                        {!['heart_sent', 'nudge_sent', 'daily_goal_reached', 'weekly_goal_reached', 'badge_earned', 'practice_reminder'].includes(event.type) && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> has a new update
                          </span>
                        )}
                      </div>
                      
                      {/* Time */}
                      <div className="flex-shrink-0">
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(event.timestamp)}
                        </span>
                      </div>
                      
                      {/* Action Button */}
                      <div className="flex-shrink-0">
                        {event.type === 'practice_reminder' ? (
                          usersStartedPractice.has(event.toUserId!) ? (
                            <span className="text-green-600 text-xs font-medium px-2 py-1">
                              ✅ Started Practice
                            </span>
                          ) : (
                            <div className="relative group">
                              {(event.nudges && event.nudges.includes(currentUser?.uid || '')) || sentNudges.has(event.id) ? (
                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                                  🤗 Nudge sent!
                                </span>
                              ) : (
                                <button
                                  onClick={() => sendNudge(event.toUserId!, event.id)}
                                  className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-medium hover:bg-orange-200 transition-colors"
                                >
                                  🤗 Nudge
                                </button>
                              )}
                              {!((event.nudges && event.nudges.includes(currentUser?.uid || '')) || sentNudges.has(event.id)) && (
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                  Send your buddy a nudge to help keep them on target!
                                </div>
                              )}
                            </div>
                          )
                        ) : event.fromUserId !== currentUser?.uid ? (
                          (event.hearts && event.hearts.includes(currentUser?.uid || '')) || sentHearts.has(event.id) ? (
                            <span className="text-pink-600 text-xs font-medium px-2 py-1">
                              💖 Heart sent!
                            </span>
                          ) : (
                            <div className="relative group">
                              <button
                                onClick={() => sendHeart(event.fromUserId, event.id)}
                                className="bg-pink-100 text-pink-700 px-2 py-1 rounded text-xs font-medium hover:bg-pink-200 transition-colors"
                              >
                                💖 Heart
                              </button>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                Send your buddy a heart for their achievement
                              </div>
                            </div>
                          )
                        ) : (
                          <span className="text-gray-400 text-xs font-medium px-2 py-1">
                            Your Achievement
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Mobile Layout - Two Lines */}
                    <div className="sm:hidden">
                      {/* Top Line - Message */}
                      <div className="mb-2">
                        {event.type === 'heart_sent' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> sent a heart to <span className="font-semibold">{event.toUserNickname}</span>
                          </span>
                        )}
                        {event.type === 'daily_goal_reached' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> reached their daily goal! 🎯
                          </span>
                        )}
                        {event.type === 'weekly_goal_reached' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> completed their weekly goal! 🏆
                          </span>
                        )}
                        {event.type === 'badge_earned' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> earned {event.badgeName ? (
                              <>the <span className="font-semibold">
                                {(() => {
                                  const badgeDef = getBadgeDefinition(event.badgeName);
                                  
                                  return badgeDef ? (
                                    <span className="relative group">
                                      <span className="cursor-help underline decoration-dotted">{event.badgeName}</span>
                                      {/* Tooltip */}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                        {badgeDef.emoji} {badgeDef.description}
                                      </div>
                                    </span>
                                  ) : (
                                    <span className="text-orange-600" title={`No definition found for: ${event.badgeName}`}>
                                      {event.badgeName}
                                    </span>
                                  );
                                })()}
                              </span> badge</>
                            ) : (
                              'a new badge'
                            )}! 🏅
                          </span>
                        )}
                        {event.type === 'practice_reminder' && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.toUserNickname}</span> hasn't started their scheduled practice yet. Give them a nudge! ⏰
                          </span>
                        )}
                        {!['heart_sent', 'nudge_sent', 'daily_goal_reached', 'weekly_goal_reached', 'badge_earned', 'practice_reminder'].includes(event.type) && (
                          <span className="text-sm text-gray-700">
                            <span className="font-semibold">{event.fromUserNickname}</span> has a new update
                          </span>
                        )}
                      </div>

                      {/* Bottom Line - Avatar, Icon, Time, Action Button */}
                      <div className="flex items-center space-x-3">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-xs">
                              {event.fromUserNickname.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        
                        {/* Icon */}
                        <div className="flex-shrink-0">
                          {event.type === 'heart_sent' && <span className="text-sm">💖</span>}
                          {event.type === 'daily_goal_reached' && <span className="text-sm">🎯</span>}
                          {event.type === 'weekly_goal_reached' && <span className="text-sm">🏆</span>}
                          {event.type === 'badge_earned' && <span className="text-sm">🏅</span>}
                          {event.type === 'practice_reminder' && <span className="text-sm">⏰</span>}
                          {!['heart_sent', 'daily_goal_reached', 'weekly_goal_reached', 'badge_earned', 'practice_reminder'].includes(event.type) && <span className="text-sm">📢</span>}
                        </div>
                        
                        {/* Time */}
                        <div className="flex-1">
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(event.timestamp)}
                          </span>
                        </div>
                        
                        {/* Action Button */}
                        <div className="flex-shrink-0">
                          {event.type === 'practice_reminder' ? (
                            usersStartedPractice.has(event.toUserId!) ? (
                              <span className="text-green-600 text-xs font-medium px-2 py-1">
                                ✅ Started
                              </span>
                            ) : (
                              <div className="relative group">
                                {(event.nudges && event.nudges.includes(currentUser?.uid || '')) || sentNudges.has(event.id) ? (
                                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                                    🤗 Sent
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => sendNudge(event.toUserId!, event.id)}
                                    className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-medium hover:bg-orange-200 transition-colors"
                                  >
                                    🤗 Nudge
                                  </button>
                                )}
                                {!((event.nudges && event.nudges.includes(currentUser?.uid || '')) || sentNudges.has(event.id)) && (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                    Send your buddy a nudge to help keep them on target!
                                  </div>
                                )}
                              </div>
                            )
                          ) : event.fromUserId !== currentUser?.uid ? (
                            (event.hearts && event.hearts.includes(currentUser?.uid || '')) || sentHearts.has(event.id) ? (
                              <span className="text-pink-600 text-xs font-medium px-2 py-1">
                                💖 Sent
                              </span>
                            ) : (
                              <div className="relative group">
                                <button
                                  onClick={() => sendHeart(event.fromUserId, event.id)}
                                  className="bg-pink-100 text-pink-700 px-2 py-1 rounded text-xs font-medium hover:bg-pink-200 transition-colors"
                                >
                                  💖 Heart
                                </button>
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                  Send your buddy a heart for their achievement
                                </div>
                              </div>
                            )
                          ) : (
                            <span className="text-gray-400 text-xs font-medium px-2 py-1">
                              Your Achievement
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  ));
                })()}
              </div>
              
              {/* Pagination Controls */}
              {(() => {
                const { totalPages } = getPaginatedEvents();
                if (totalPages <= 1) return null;
                
                return (
                  <div className="p-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      <div className="flex items-center gap-2">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-1 text-sm rounded-md ${
                                currentPage === pageNum
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Feed;
